import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { adminApi } from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const empty = { slug: '', title: '', excerpt: '', content: '', image: '', author: 'Get Nile Cruise Team', tags: [], published: true };

const AdminBlog = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { setLoading(true); try { setItems(await adminApi.listBlog()); } catch {} setLoading(false); };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const tags = typeof form.tags === 'string' ? form.tags.split(',').map((x) => x.trim()).filter(Boolean) : form.tags;
      const payload = { ...form, tags, slug: form.slug || slugify(form.title) };
      if (editing) await adminApi.updateBlog(editing.id, payload); else await adminApi.createBlog(payload);
      toast({ title: 'Saved' }); setOpen(false); load();
    } catch (err) { toast({ title: 'Save failed', description: err.response?.data?.detail, variant: 'destructive' }); }
  };

  const del = async (item) => { if (!window.confirm(`Delete "${item.title}"?`)) return; try { await adminApi.deleteBlog(item.id); load(); } catch { toast({ title: 'Delete failed', variant: 'destructive' }); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-3xl text-[#304b71]">Blog Posts</h1><p className="text-slate-500 text-sm mt-1">Travel guide articles and SEO content.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#304b71] hover:bg-[#6a8aa4] text-white"><Plus size={16} className="mr-1" /> New Post</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-[#304b71]">{editing ? 'Edit' : 'New'} Blog Post</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Title *</Label><Input required value={form.title} onChange={upd('title')} className="mt-1" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={upd('slug')} placeholder={slugify(form.title)} className="mt-1" /></div>
              <div><Label>Image URL *</Label><Input required value={form.image} onChange={upd('image')} className="mt-1" /></div>
              <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={upd('excerpt')} className="mt-1" /></div>
              <div><Label>Content</Label><Textarea rows={10} value={form.content} onChange={upd('content')} className="mt-1" /></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Author</Label><Input value={form.author} onChange={upd('author')} className="mt-1" /></div>
                <div><Label>Tags (comma-separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(', ') : form.tags} onChange={upd('tags')} className="mt-1" /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /><Label>Published</Label></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" className="bg-[#304b71] hover:bg-[#6a8aa4] text-white">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#304b71]" /></div> :
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-[#f6f8fa]"><tr><th className="text-left px-5 py-3">Title</th><th className="text-left">Status</th><th className="text-left">Date</th><th className="text-right px-5">Actions</th></tr></thead>
            <tbody>{items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={p.image} alt="" className="w-14 h-14 object-cover rounded-lg" /><div><div className="font-serif text-[#304b71]">{p.title}</div><div className="text-xs text-slate-500">{p.slug}</div></div></div></td>
                <td>{p.published ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Published</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Draft</span>}</td>
                <td className="text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="text-right px-5 space-x-1"><Button size="sm" variant="outline" onClick={() => { setEditing(p); setForm({ ...p, tags: p.tags || [] }); setOpen(true); }}><Pencil size={14} /></Button><Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => del(p)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}</tbody>
          </table>
        }
      </div>
    </div>
  );
};

export default AdminBlog;
